import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactLoading from 'react-loading';
import { standardDate } from '../../../utils/formatDate';
import EmptyPage from '../../empty-page/emptyPage';
import Dialog from '../../dialogModal/dialogModal';
import { fetchActBookingData, fetchActBookingsCount, fetchActListdata } from './orgActBookingFn/orgActBookingFn';
import Pagination from '../../pagination/Pagination';
import CommonTable from '../../reusable-ui-components/commonTable/CommonTable';
import { getTotalSeatCount } from '../../../utils/getTotalSeatCount';

const OrgActivityBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [actsData, setActsData] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedType = searchParams.get('type') || 'upcoming';
    const selectedAct = searchParams.get('act') || '';
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [rowClickId, setRowClickId] = useState(0);
    const pageNumber = Number(searchParams.get('page')) || 1;
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const [totalCount, setTotalCount] = useState(0);
    const totalData = totalCount;
    const totalPages = Math.ceil(totalData / limit);
    const [messageData, setMessageData] = useState({
        msgType: 'whatsApp',
        whatsApp: '',
        message: ''
    });

    // const tableHeader = [
    //     'Activity Name',
    //     'Name',
    //     'Place',
    //     'Date',
    //     'Booked On',
    //     'Payment',
    //     'Total Seats',
    //     'Booked Via',
    //     'People',
    //     'Status'
    // ];

    const columns = [
        {
            label: 'Activity Name',
            accessor: 'actId',
            render: (val) => val.title
        },
        {
            label: 'Name',
            accessor: 'userDetails',
            render: (val) => val?.[0]?.name || 'N/A'
        },
        {
            label: 'Place',
            accessor: 'actId',
            render: (val) => val?.quickView.baseCities || 'N/A'
        },
        {
            label: 'Time Slot',
            accessor: 'timeSlots',
            render: (val) => {
                if (!val) return '-';
                return `${val.startTime} - ${val.endTime}`;
            }
        },
        {
            label: 'Date',
            accessor: 'startDate',
            render: (val) => new Date(val).toLocaleDateString()
        },
        {
            label: 'Booked On',
            accessor: 'createdAt',
            render: (val) => new Date(val).toLocaleDateString()
        },
        {
            label: 'Payment',
            accessor: 'amountPaid',
            render: (val, row) => {
                const isReserved = Boolean(val?.isReserved);
                const isInstallment = Boolean(val?.isInstallment);

                if (!isReserved && !isInstallment) {
                    return <div style={{ textAlign: 'center' }}>Full payment<br />Paid: ₹{Number(val?.paid) || 0}</div>;
                }

                const total = Number(val?.total) || 0;
                const paid = Number(val?.paid) || 0;

                const totalSeats = getTotalSeatCount(row?.totalSeats) || row?.userDetails?.length || 0;

                let balance = 0;
                let paymentText = '';

                // ---------------------------------------
                // RESERVED BOOKING
                // ---------------------------------------
                if (isReserved) {
                    // reserve is an ARRAY
                    const reservePerSeat = Number(val?.reserve?.[0]?.amount) || 0;

                    const reservedAmount = reservePerSeat * totalSeats;

                    balance = Math.max(0, total - reservedAmount);

                    paymentText = `(₹${reservePerSeat}/seat)`;
                }

                // ---------------------------------------
                // INSTALLMENT BOOKING
                // ---------------------------------------
                if (isInstallment) {
                    const installmentPaid = Array.isArray(val?.installments)
                        ? val.installments.reduce((sum, installment) => {
                              return sum + (Number(installment?.amount) || Number(installment?.total) || 0);
                          }, 0)
                        : 0;

                    balance = Math.max(0, total - installmentPaid);

                    paymentText = `(₹${installmentPaid})`;
                }

                return (
                    <div
                        style={{
                            color: 'red',
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                            verticalAlign: 'middle'
                        }}
                    >
                        {`${isReserved ? 'Reserved' : 'Installment'}\n${paymentText}
Total: ₹${total}
Paid: ₹${paid}
Balance: ₹${balance}`}
                    </div>
                );
            }
        },
        {
            label: 'Total Seats',
            accessor: 'totalSeats',
            render: (val) =>
                val?.map((seats) => (
                    <div>
                        {seats.name} - {seats.qty}
                    </div>
                ))
        },
        {
            label: 'Booked Via',
            accessor: 'bookedVia'
        },
        {
            label: 'Status',
            accessor: 'actStatus',
            render: (val) => (
                <span
                    style={{
                        color: val === 'Cancelled' ? 'red' : ''
                    }}
                >
                    {val || 'N/A'}
                </span>
            )
        }
    ];

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        fetchActBookingsCount(selectedType, setTotalCount, setLoading, selectedAct);
    }, [selectedType, selectedAct]);

    useEffect(() => {
        fetchActBookingData(selectedType, offSet, limit, setBookings, setLoading, selectedAct);
        fetchActListdata(selectedAct, setActsData, setLoading);
    }, [selectedType, selectedAct, offSet, limit]);

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalData / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    const getTripDetails = (row) => {
        return (
            <div className='bookingTitleArea'>
                <div>{row?.actId?.title ?? 'N/A'}</div>
                {/* <div>{standardDate(row.startDate.split('T')[0])} </div> */}
            </div>
        );
    };

    const getUserDetails = (row) => {
        return row.userDetails.map(function (user, userKey) {
            return (
                <div key={userKey}>
                    <span>{user?.name ?? 'N/A'}, </span>
                    <span>{user?.age ?? 'N/A'} years, </span>
                    <span>{user?.sex ?? 'N/A'}, </span>
                    <span>{user?.phoneNumber ?? 'N/A'}</span>
                </div>
            );
        });
    };

    return (
        <>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='bookingSticky'>
                <div className='alignCenter'>
                    <h2 className='orgBookingHeading'>Bookings</h2>
                    <div className='orgFilterWrapper'>
                        {/* <div className='orgTripDateInfo mb-2'>
                            <span className='orgTripDateSpan'>Bookings Today</span>
                            <span className='orgTripDateSpan'>{todaysBookings.bookedToday ?? '0'}</span>
                        </div> */}
                    </div>
                </div>
                <div className='orgBookingFilters'>
                    <select
                        name='type'
                        id='type'
                        className='select'
                        value={selectedType}
                        onChange={(e) => {
                            updateURL({ type: e.target.value, page: 1 });
                        }}
                    >
                        <option value='upcoming'>Upcoming</option>
                        <option value='completed'>Completed</option>
                    </select>
                    {selectedType && (
                        <select
                            name='trip'
                            id='trip'
                            className='select'
                            value={selectedAct}
                            onChange={(e) => {
                                updateURL({ act: e.target.value, page: 1 });
                            }}
                        >
                            <option value=''>Select Activity</option>
                            {actsData.map((actItem) => (
                                <option value={actItem._id} key={actItem._id}>
                                    {actItem.title}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>
            <div className='orgBookingContainer'>
                {bookings?.length === 0 && !loading ? (
                    <EmptyPage />
                ) : (
                    <>
                        <CommonTable data={bookings} columns={columns} setRowClickId={setRowClickId} />
                        <Pagination
                            paginationClick={paginationClick}
                            numberOfRows={totalData}
                            offSet={offSet}
                            pageNumber={pageNumber}
                            limit={limit}
                        />
                    </>
                )}
                {modal && (
                    <Dialog openModal={modal} heading={'Send Message'}>
                        <div className='dialogContent'>
                            <select
                                name='date'
                                id='date'
                                className='select w-full'
                                value={'whatsApp'}
                                onChange={(e) => {}}
                                disabled
                            >
                                <option value=''>Select Message Type</option>
                                {/* {messageType.map((type) => (
                                    <option value={type.value} key={type.value}>
                                        {type.name}
                                    </option>
                                ))} */}
                            </select>
                            {messageData.msgType === 'whatsApp' && (
                                <input
                                    placeholder='Whatsapp group link'
                                    name='whatsapp'
                                    onChange={(e) => {}}
                                    // value={messageData.whatsApp}
                                    className='whatsAppLink'
                                />
                            )}
                            {/* {messageData.msgType !== 'rate' &&
                                messageData.msgType !== 'whatsApp' && (
                                    <>
                                        <textarea
                                            className="messageArea"
                                            value={messageData.message}
                                            onChange={(e) =>
                                                setMessageData({
                                                    ...messageData,
                                                    message: e.target.value
                                                })
                                            }
                                        />
                                        <CharCount
                                            string={
                                                messageData.message
                                                    ? messageData.message
                                                    : ''
                                            }
                                            min={10}
                                            limit={50}
                                        />
                                    </>
                                )} */}
                            <p className='note'>
                                Note:{' '}
                                <span>The message will be sent to all the customers in this particular trip date</span>
                            </p>
                            <div className='alignCenter'>
                                <button className='dialogActBtn cancel' onClick={() => setModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    className='dialogActBtn submit'
                                    // disabled={!messageData.msgType}
                                    onClick={() => {}}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </Dialog>
                )}
            </div>
        </>
    );
};

export default OrgActivityBookings;
